import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import * as _ from 'lodash';

import { WikiService } from '../services/wiki.service';
import { UiService } from '../../../core/services/core.ui.service';
import { GfTranslateService } from '../../../core/services/core.translate.service';

/**
 * Rename dialog for a wiki page: collects the new name and confirms the rename.
 *
 * As the name is typed the dialog asks the backend to rename with `dryRun`, which runs every
 * validation and computes the full rewrite plan without writing anything. That gives the user
 * an exact count of the pages that will change — and surfaces a name collision or a reserved
 * name before anything happens — using the same code path the real rename will take, so the
 * preview cannot drift from the outcome.
 *
 * A host that already collected a name (the notebook, which renames from its own page list)
 * passes it as {@link newPagename}; the field opens pre-filled with it and the preview runs
 * immediately. A host that has no name yet (the wiki editor's Rename button) passes nothing and
 * the field opens on the current name, with no preview until it actually changes.
 *
 * Rewriting referrers is not offered as a choice. A wiki link is text that is resolved by
 * name at render time, so leaving it stale is never the right answer; the dialog reports
 * what will change rather than asking whether it should.
 */
@Component({
    selector: 'wiki-rename-modal',
    template: require('./wiki.rename.modal.component.html'),
})
export class WikiRenameModalComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() wiki: any;
    @Input() project: any;
    /** Optional starting name. Empty ⇒ the field opens on the page's current name. */
    @Input() newPagename: string = '';
    /** Called with the server's rename report once the rename succeeds. */
    @Input() onRenamed: Function;
    @Input() onClose: Function;

    @ViewChild('pagenameInput', { static: false }) pagenameInput: ElementRef;

    /** Matches WikiBackendService::PAGENAME_MAX_LENGTH — the backend rejects anything longer. */
    readonly maxLength: number = 50;

    /** Referrer rows from the dry run: { id, pagename, rewrites, forms, unlinkedMentions }. */
    referrers: any[] = [];
    pagesChanged: number = 0;
    totalRewrites: number = 0;
    unlinkedMentions: number = 0;
    loading: boolean = false;
    renaming: boolean = false;
    /** Validation message from the dry run (collision, reserved name, illegal characters). */
    error: string = '';

    /**
     * Preview the name the user has stopped typing, not every keystroke: the dry run is a
     * server round trip that walks every referrer.
     */
    private readonly previewPlan = _.debounce(() => this.runDryRun(), 400);

    /**
     * Identifies the preview the dialog is currently interested in. Bumped whenever the name
     * changes or a rename starts, which is what makes an in-flight dry run's reply stale.
     */
    private previewToken: number = 0;

    constructor(
        private wikiService: WikiService,
        private uiService: UiService,
        private translateService: GfTranslateService,
    ) {}

    ngOnInit(): void {
        if (!this.newPagename) {
            this.newPagename = this.currentPagename;
            return;
        }
        // Opened with a name already chosen — show its impact straight away.
        this.runDryRun();
    }

    ngAfterViewInit(): void {
        // Focus by hand: `autofocus` is only honoured on parse, and the dialog's content is
        // created after the document has loaded.
        const el = this.pagenameInput?.nativeElement ?? null;
        if (el) { setTimeout(() => { el.focus(); el.select(); }); }
    }

    ngOnDestroy(): void {
        this.previewPlan.cancel();
    }

    /** The page's stored name — the field's starting value and the baseline for a change. */
    get currentPagename(): string {
        return _.get(this.wiki, 'pagename', '');
    }

    /** The typed name, trimmed the same way the backend trims it. */
    get trimmedPagename(): string {
        return (this.newPagename || '').trim();
    }

    /** True once the field holds a usable name that differs from the stored one. */
    get nameChanged(): boolean {
        const trimmed = this.trimmedPagename;
        return trimmed.length > 0 && trimmed !== this.currentPagename;
    }

    /**
     * The preview is advice, not a gate: a user who types a name and hits Enter inside the
     * debounce window should still get the rename, so a pending dry run does not disable this.
     * The backend runs the same validation on the real rename and the dialog shows what it says.
     */
    get canRename(): boolean {
        return this.nameChanged && !this.renaming && !this.error;
    }

    /**
     * Restart the preview for a newly typed name.
     *
     * The old plan is dropped immediately rather than left on screen while the new one loads:
     * a rewrite count that belongs to a name the user has already changed is worse than none.
     */
    onPagenameChange(): void {
        this.previewPlan.cancel();
        this.previewToken++;
        this.clearPlan();
        this.error = '';

        if (!this.nameChanged) { this.loading = false; return; }

        this.loading = true;
        this.previewPlan();
    }

    /**
     * Lead-in for the referrer list.
     *
     * Built in TypeScript rather than the template so the count and the sentence stay a single
     * translatable string with an interpolated parameter: splitting them into a bare
     * `{{pagesChanged}}` next to a translated fragment both fixes English word order for every
     * language and is what produced the missing space in "1pages link to this page".
     */
    get referrerSummary(): string {
        return this.translateService.translate(
            '{{count}} pages link to this page. The links will be updated after rename.',
            { count: this.pagesChanged }
        );
    }

    /** Referrers that will actually be edited, for the preview list. */
    get changedReferrers(): any[] {
        return this.referrers.filter((r: any) => r.rewrites > 0);
    }

    /** Referrers that mention the old name only as prose, which is left untouched. */
    get mentionOnlyReferrers(): any[] {
        return this.referrers.filter((r: any) => r.rewrites === 0 && r.unlinkedMentions > 0);
    }

    doCancel(): void {
        this.previewPlan.cancel();
        this.onClose();
    }

    doRename(): void {
        if (!this.canRename) { return; }

        const newName = this.trimmedPagename;
        // The preview is only advice; nothing is gained by letting a queued or in-flight one
        // land on top of the rename it was previewing — including onto its error message.
        this.previewPlan.cancel();
        this.previewToken++;
        this.loading = false;
        this.renaming = true;

        this.wikiService.renameWiki(this.wiki.id, newName, false)
            .then((report: any) => {
                this.uiService.message.showSuccess(
                    this.summaryMessage(report, newName)
                );
                this.onRenamed(report);
                this.onClose();
            })
            .catch((response: any) => {
                this.renaming = false;
                this.error = this.messageFrom(response);
            });
    }

    /**
     * Ask the backend what the rename would do, without doing it.
     *
     * Typing races the network, so a reply is applied only while it is still the preview the
     * dialog asked for: an earlier, slower one must not overwrite a later name's plan, and none
     * of them may overwrite the outcome of a rename that has since started.
     */
    private runDryRun(): void {
        const newName = this.trimmedPagename;
        if (!this.nameChanged) { this.loading = false; return; }

        const token = ++this.previewToken;
        const isCurrent = () => token === this.previewToken;
        this.loading = true;

        this.wikiService.renameWiki(this.wiki.id, newName, true)
            .then((report: any) => {
                if (!isCurrent()) { return; }
                this.referrers = (report && report.referrers) || [];
                this.pagesChanged = (report && report.pagesChanged) || 0;
                this.totalRewrites = (report && report.totalRewrites) || 0;
                this.unlinkedMentions = (report && report.unlinkedMentions) || 0;
            })
            .catch((response: any) => {
                if (!isCurrent()) { return; }
                this.clearPlan();
                this.error = this.messageFrom(response);
            })
            .finally(() => {
                if (isCurrent()) { this.loading = false; }
            });
    }

    private clearPlan(): void {
        this.referrers = [];
        this.pagesChanged = 0;
        this.totalRewrites = 0;
        this.unlinkedMentions = 0;
    }

    /**
     * Report what the rename actually did. The rewrites are part of the same transaction, so
     * by the time this runs the counts describe finished work, not pending work.
     */
    private summaryMessage(report: any, newName: string): string {
        const name = (report && report.pagename) || newName;

        if (report && report.pagesChanged > 0) {
            return this.translateService.translate(
                'Renamed to "{{pagename}}" and updated links on {{count}} pages.',
                { pagename: name, count: report.pagesChanged }
            );
        }

        return this.translateService.translate(
            'Renamed to "{{pagename}}".', { pagename: name }
        );
    }

    /**
     * Pull the server's validation message out of the error response.
     *
     * A rejected rename comes back as HTTP 400 whose body is the exception's validation-error
     * array — `{ pagename: "A page named 'X' already exists." }` — because
     * RestExceptionMapper serialises those errors and drops the exception message. Read that
     * key first; the remaining shapes cover non-validation failures.
     */
    private messageFrom(response: any): string {
        const body = response?.error;
        const message = body?.pagename || body?.message || body?.error || response?.message;
        return message || this.translateService.translate('The page could not be renamed.');
    }
}
